// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import type { Language } from '../config/localization';

interface LanguageSwitcherProps {
  currentLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

interface LanguageOption {
  code: Language;
  name: string;
  flag: string;
}

const LANGUAGES: LanguageOption[] = [
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇺🇸' }
];

// Функция для определения языка системы
const getSystemLanguage = (): Language => {
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('ru')) {
    return 'ru';
  }
  return 'en'; // английский по умолчанию
};

// Функция для инициализации языка (из localStorage или определение системного)
export const initializeLanguage = (): Language => {
  try {
    const saved = localStorage.getItem('preferred-language');
    if (saved === 'ru' || saved === 'en') {
      return saved as Language;
    }
  } catch (error) {
    console.warn('Could not read from localStorage:', error);
  }
  
  return getSystemLanguage();
};

// Функция для сохранения языка в localStorage
export const saveLanguagePreference = (language: Language): void => {
  try {
    localStorage.setItem('preferred-language', language);
  } catch (error) {
    console.warn('Could not save to localStorage:', error);
  }
};

export default function LanguageSwitcher({ currentLanguage, onLanguageChange }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Закрытие дропдауна при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Закрытие дропдауна по Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const currentLang = LANGUAGES.find(lang => lang.code === currentLanguage) || LANGUAGES[1];

  const handleLanguageSelect = (language: Language) => {
    setIsOpen(false);
    onLanguageChange(language);
    saveLanguagePreference(language);
  };

  return (
    <div 
      className="relative" 
      ref={dropdownRef}
      data-testid="language-switcher"
      data-current-language={currentLanguage}
    >
      {/* Кнопка переключателя */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 transition-colors"
        title={`Язык / Language: ${currentLang.name}`}
      >
        <div className="flex items-center space-x-2">
          <span className="text-base">{currentLang.flag}</span>
          <span className="text-white font-medium">
            {currentLang.name}
          </span>
        </div>
        
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Выпадающий список */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
          <div className="p-2">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageSelect(language.code)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  currentLanguage === language.code 
                    ? 'bg-purple-500/20 border border-purple-500/50' 
                    : 'hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{language.flag}</span>
                  <span className="text-sm font-medium text-white">{language.name}</span>
                  {currentLanguage === language.code && (
                    <svg className="w-4 h-4 ml-auto text-purple-400" viewBox="0 0 24 24" fill="none">
                      <path 
                        d="M20 6L9 17L4 12" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
}